// Название встречи в календаре в формате психолога: «ИК Вероника тг 74 Жёлтая рубашка».
// Код группы (ИК, ПК, хПК), имя с приметами, номер сессии, хвост после номера; «?» — резерв; «отмена…» — отмена.
import { normSig } from './statements.ts';

export const MSG_TOKEN: Readonly<Record<string, string>> = {
  Telegram: 'тг', WhatsApp: 'вотсап', 'ВКонтакте': 'ВК', Viber: 'вайбер',
};

// Собрать название обратно: пустые части пропускаются.
export function calTitle(code: string, namePart: string, num: number | string | null | undefined, tail: string): string {
  return [code, namePart, num ? String(num) : '', tail].filter((x) => x !== '' && x !== null && x !== undefined).join(' ');
}

// «?» у резерва ставится там же, где его ставит она: в начале («? ИК Вероника») или в конце.
export function reserveTitle(mark: 'start' | 'end', title: string): string {
  return mark === 'end' ? title + ' ?' : '? ' + title;
}

export interface CancelMark {
  kind: '' | 'late' | 'ok';   // late — поздняя (оплачивается), ok — вовремя (без оплаты)
  part: number;               // доля оплаты: 1, 0.5 («половинка»), 0
  words: string;              // сами слова, как написаны
  rest: string;               // текст без них
  atEnd: boolean;             // слова стоят в самом конце
}

// Отмена словами — как пишет Дарья: «отмена» — вовремя, без оплаты; «отмена оплатила», «отмена оплачена»,
// «отмена с оплатой» — поздняя, оплачена; «отмена половинка», «отмена 50%» — поздняя, половина.
// «оплачено» без «отмена» — не отмена; «Отменкова» — фамилия. Берётся последнее такое слово.
const CANCEL_RE = new RegExp('(^|[\\s(,;.·/-])(отмен(?:а|ена|ено|ён|ен|ил|ила|или|ился|илась|ились))' +
  '((?:\\s+(?:с\\s+)?(?:оплат[а-яё]*|оплач[а-яё]*|половин[а-яё]*|50\\s*%))*)(?=$|[\\s),;.!·/-])', 'ig');

export function cancelMark(text: string | null | undefined): CancelMark {
  const t = String(text ?? '');
  let last: RegExpExecArray | null = null;
  for (const m of t.matchAll(CANCEL_RE)) last = m as RegExpExecArray;
  if (!last) return { kind: '', part: 0, words: '', rest: t, atEnd: false };
  const before = last[1] ?? '', word = last[2] ?? '', more = last[3] ?? '', end = last.index + last[0].length;
  const half = /половин|50/i.test(more), paid = half || /оплат|оплач/i.test(more);
  const rest = (t.slice(0, last.index) + before + t.slice(end)).replace(/\(\s*\)/g, ' ').replace(/\s+/g, ' ')
    .replace(/^[\s·]+|[\s·]+$/g, '');
  return { kind: paid ? 'late' : 'ok', part: half ? 0.5 : (paid ? 1 : 0), words: (word + more).trim(), rest,
    atEnd: !t.slice(end).replace(/[\s).,;!·]+/g, '') };
}

export interface ParsedTitle {
  flag: '' | '!' | '!!';
  code: string;
  name: string;          // имя без слова связи
  rawName: string;       // как записано до номера, вместе со словом связи — чтобы писать встречи тем же видом
  num: string;           // номер сессии
  tail: string;          // после номера, без слова связи
  rawTail: string;       // после номера как написано
  messenger: string;     // Telegram, WhatsApp, ВКонтакте, Viber
  key: string;           // имя для сравнения
  reserve: boolean;
  reservePos: '' | 'start' | 'end';
  cancel: '' | 'late' | 'ok';
  cancelPart: number;
  cancelWords: string;
}

const MESSENGERS: ReadonlyArray<readonly [RegExp, string]> = [
  [/(^|\s)(тг|тлг|телега|телеграм|телеграмм|telegram|tg)(?=\s|$)/i, 'Telegram'],
  [/(^|\s)(вотсап|ватсап|вацап|воцап|вотсапп|whatsapp|wa)(?=\s|$)/i, 'WhatsApp'],
  [/(^|\s)(вк|vk|вконтакте)(?=\s|$)/i, 'ВКонтакте'],
  [/(^|\s)(вайбер|viber)(?=\s|$)/i, 'Viber'],
];

// «! ИК Надежда» → флаг «!», код «ИК», имя «Надежда». «хПК» — код целиком (бывший корпоративный).
// Номер — счётчик сессий, в имя не входит; всё после номера — примечание к встрече.
export function parseEventTitle(summary: string | null | undefined): ParsedTitle {
  let t = String(summary ?? '').replace(/\s+/g, ' ').trim();
  const reserve = /\?/.test(t);
  const reservePos: ParsedTitle['reservePos'] = reserve ? (/^[!‼❗\s]*\?/.test(t) ? 'start' : 'end') : '';
  if (reserve) t = t.replace(/\?+/g, ' ').replace(/\s+/g, ' ').trim();
  const cx = cancelMark(t);
  if (cx.kind) t = cx.rest;
  let flag: ParsedTitle['flag'] = '', code = '';
  let m = t.match(/^([!‼❗]+)\s*/);
  if (m) { flag = (m[1] ?? '').length > 1 || m[1] === '‼' ? '!!' : '!'; t = t.slice(m[0].length); }
  m = t.match(/^([хx]?[A-ZА-ЯЁ]{2,4})\s+(?=\S)/);
  if (m) { code = (m[1] ?? '').replace(/^x/, 'х'); t = t.slice(m[0].length); }
  t = t.replace(/\s*\(.*$/, '').trim();
  let name = t, num = '', tail = '';
  m = t.match(/^(.*?\S)\s+(?:№|#|N\s?)?(\d{1,4})(?:\s+(.*))?$/);
  if (m) { name = (m[1] ?? '').trim(); num = m[2] ?? ''; tail = (m[3] ?? '').trim(); }
  let messenger = '';
  const strip = (txt: string): string => {
    let out = txt;
    for (const [re, label] of MESSENGERS) {
      if (re.test(out)) { if (!messenger) messenger = label; out = out.replace(re, ' '); }
    }
    return out.replace(/\s+/g, ' ').trim();
  };
  const rawName = name, rawTail = tail;
  const clean = strip(name);
  if (clean) name = clean;
  tail = strip(tail);
  return { flag, code, name, rawName, num, tail, rawTail, messenger, key: normSig(name), reserve, reservePos,
    cancel: cx.kind, cancelPart: cx.part, cancelWords: cx.words };
}
